import { useState, useEffect } from 'react'
import { FileText, Save, Maximize2, Minimize2, X } from 'lucide-react'

interface NotepadProps {
  isOpen: boolean
  onToggle: () => void
  sessionId: string
}

export default function Notepad({ isOpen, onToggle, sessionId }: NotepadProps) {
  const [notes, setNotes] = useState('')
  const [isMinimized, setIsMinimized] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Load saved notes on mount
  useEffect(() => {
    const savedNotes = localStorage.getItem(`notepad_${sessionId}`)
    if (savedNotes) {
      setNotes(savedNotes)
    }
  }, [sessionId])

  // Auto-save notes
  useEffect(() => {
    if (notes.trim() && sessionId) {
      const timeoutId = setTimeout(() => {
        saveNotes()
      }, 2000) // Auto-save after 2 seconds of inactivity

      return () => clearTimeout(timeoutId)
    }
  }, [notes, sessionId])

  const saveNotes = async () => {
    if (!sessionId) return
    
    setIsSaving(true)
    try {
      // Save to localStorage as primary storage
      localStorage.setItem(`notepad_${sessionId}`, notes)
      setLastSaved(new Date())
    } catch (error) {
      console.error('Failed to save notes:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleNotesChange = (value: string) => {
    setNotes(value)
  }

  const formatLastSaved = () => {
    if (!lastSaved) return ''
    return `Saved at ${lastSaved.toLocaleTimeString()}`
  }

  if (!isOpen) return null

  return (
    <div className="fixed top-20 right-4 z-50">
      <div className={`bg-white border border-gray-300 rounded-lg shadow-lg transition-all duration-300 ${
        isMinimized ? 'w-64 h-12' : 'w-80 h-96'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex items-center space-x-2">
            <FileText className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Notes</span>
            {isSaving && (
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
              title={isMinimized ? 'Maximize' : 'Minimize'}
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={onToggle}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="flex flex-col h-80">
            <textarea
              value={notes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Type your notes here... They will auto-save every 2 seconds."
              className="flex-1 p-3 border-none outline-none resize-none text-sm text-gray-700 placeholder-gray-400"
              style={{ fontFamily: 'inherit' }}
            />
            
            {/* Footer */}
            <div className="p-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {formatLastSaved()}
                </span>
                <button
                  onClick={saveNotes}
                  disabled={isSaving}
                  className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                >
                  <Save className="w-3 h-3" />
                  <span>{isSaving ? 'Saving...' : 'Save'}</span>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Minimized content */}
        {isMinimized && (
          <div className="px-3 py-2">
            <span className="text-xs text-gray-500">
              {notes.length > 0 ? `${notes.length} characters` : 'No notes yet'}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}